import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, CheckCircle2, Circle, Clock } from 'lucide-react';
import { getMilestoneStatusLabel } from '../types';
import HoursProgressBar from './HoursProgressBar';
import type { MilestoneStatus, Ticket } from '../types';
import DeadlineBadge from './DeadlineBadge';

interface MilestoneCardProps {
  id: string;
  name: string;
  description?: string;
  dueDate?: string;
  status: MilestoneStatus;
  ticketsTotal: number;
  ticketsClosed: number;
  estimatedHours?: number;
  actualHours: number;
  tasks?: Ticket[];
  onReorder?: (tasks: Ticket[]) => void;
}

const statusIcon: Record<MilestoneStatus, typeof Circle> = {
  pending: Circle,
  in_progress: Clock,
  completed: CheckCircle2,
};

const statusColors: Record<MilestoneStatus, string> = {
  pending: 'text-surface-400',
  in_progress: 'text-info-500',
  completed: 'text-success-500',
};

export default function MilestoneCard({
  id, name, description, dueDate, status,
  ticketsTotal, ticketsClosed, estimatedHours, actualHours, tasks,
}: MilestoneCardProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const StatusIcon = statusIcon[status];
  const taskPct = ticketsTotal > 0 ? Math.round((ticketsClosed / ticketsTotal) * 100) : 0;

  return (
    <div className="border border-surface-400/15 rounded-xl bg-surface-0 overflow-hidden">
      <button onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-surface-50 transition-colors text-right">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <StatusIcon size={20} className={statusColors[status]} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-semibold text-surface-800 truncate">{name}</h4>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                status === 'completed' ? 'bg-success-100 text-success-700' :
                status === 'in_progress' ? 'bg-info-100 text-info-700' :
                'bg-surface-100 text-surface-600'
              }`}>
                {getMilestoneStatusLabel(status, t)}
              </span>
            </div>
            <div className="flex items-center gap-3 mt-1 text-xs text-surface-500">
              <span>{t('milestone.taskCount', { closed: ticketsClosed, total: ticketsTotal, pct: taskPct })}</span>
              {dueDate && <DeadlineBadge dueDate={dueDate} source="milestone" />}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {estimatedHours && estimatedHours > 0 && (
            <div className="w-32 hidden sm:block">
              <HoursProgressBar actual={actualHours} estimated={estimatedHours} showLabel={false} />
            </div>
          )}
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-surface-400/10">
          {description && <p className="text-sm text-surface-600 mt-3 mb-3">{description}</p>}

          {estimatedHours && estimatedHours > 0 && (
            <div className="mb-3">
              <HoursProgressBar actual={actualHours} estimated={estimatedHours} />
            </div>
          )}

          {tasks && tasks.length > 0 ? (
            <div className="space-y-2 mt-3">
              {tasks.map((task) => (
                <Link key={task.id} to={`/tickets/${task.id}`}
                  className="flex items-center justify-between p-2 bg-surface-50 rounded-lg hover:bg-surface-100 transition-colors">
                  <div className="flex items-center gap-2 min-w-0">
                    {task.status === 'closed' ? (
                      <CheckCircle2 size={14} className="text-success-500 flex-shrink-0" />
                    ) : (
                      <Circle size={14} className="text-surface-400 flex-shrink-0" />
                    )}
                    <span className={`text-sm truncate ${task.status === 'closed' ? 'text-surface-400 line-through' : 'text-surface-700'}`}>
                      {task.title}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-surface-400 flex-shrink-0">
                    {task.agent_name && <span>{task.agent_name}</span>}
                    {task.estimated_hours && <span>{task.actual_hours}/{task.estimated_hours} {t('common.hoursShort')}</span>}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-xs text-surface-400 mt-3 text-center py-2">{t('milestone.noTasksInCard')}</p>
          )}

          <div className="mt-3 text-center">
            <Link to={`/milestones/${id}`} className="text-xs text-primary-600 hover:text-primary-800">
              {t('milestone.viewDetails')}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
